import { __extends } from "tslib";
import { MDCRipple } from '@material/ripple';
import { addClass, getShadowElement, removeClass, requireParent } from '@tyler-components-web/core';
import { isDeepEqual, toggleClass } from '../../core';
import { BaseAdapter } from '../../core/adapters/base-adapter';
import { LIST_CONSTANTS } from '../list/list-constants';
import { LIST_ITEM_CONSTANTS } from './list-item-constants';
var ListItemAdapter = /** @class */ (function (_super) {
    __extends(ListItemAdapter, _super);
    function ListItemAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._initialize();
        return _this;
    }
    ListItemAdapter.prototype._initialize = function () {
        this._listItemElement = getShadowElement(this._component, LIST_ITEM_CONSTANTS.selectors.LIST_ITEM);
        this._defaultSlot = getShadowElement(this._component, LIST_ITEM_CONSTANTS.selectors.DEFAULT_SLOT);
    };
    ListItemAdapter.prototype.getListItem = function () {
        return this._component;
    };
    /**
     * Adds an event listener to the `<tcw-list-item>` host element.
     * @param {string} type The event type.
     * @param {Function} listener The event callback.
     */
    ListItemAdapter.prototype.addListener = function (type, listener, options) {
        this._listItemElement.addEventListener(type, listener, options);
    };
    /**
     * Removes an event listener from the `<tcw-list-item>` host element.
     * @param {string} type The event type.
     * @param {Function} listener The event callback.
     */
    ListItemAdapter.prototype.removeListener = function (type, listener) {
        this._listItemElement.removeEventListener(type, listener);
    };
    /**
     * Creates a ripple instance on the list item.
     * @returns {MDCRipple}
     */
    ListItemAdapter.prototype.createRipple = function () {
        return new MDCRipple(this._listItemElement);
    };
    /**
     * Toggles the static state of this list item.
     * @param {boolean} value The static state.
     */
    ListItemAdapter.prototype.setStatic = function (value) {
        if (value) {
            this._listItemElement.tabIndex = -1;
            addClass(LIST_ITEM_CONSTANTS.classes.STATIC, this._listItemElement);
        }
        else {
            this._listItemElement.tabIndex = 0;
            removeClass(LIST_ITEM_CONSTANTS.classes.STATIC, this._listItemElement);
        }
    };
    /**
     * Sets this list item as a two-line list item.
     * @param {boolean} value The two-line state.
     */
    ListItemAdapter.prototype.setTwoLine = function (value) {
        if (value) {
            addClass(LIST_ITEM_CONSTANTS.classes.TWO_LINE, this._listItemElement);
        }
        else {
            removeClass(LIST_ITEM_CONSTANTS.classes.TWO_LINE, this._listItemElement);
        }
    };
    /**
     * Sets this list item as a three-line list item.
     * @param {boolean} value The three-line state.
     */
    ListItemAdapter.prototype.setThreeLine = function (value) {
        if (value) {
            addClass(LIST_ITEM_CONSTANTS.classes.THREE_LINE, this._listItemElement);
        }
        else {
            removeClass(LIST_ITEM_CONSTANTS.classes.THREE_LINE, this._listItemElement);
        }
    };
    /**
     * Determines how many lines the list item is displaying.
     */
    ListItemAdapter.prototype.getLineCount = function () {
        return this._defaultSlot.assignedNodes().filter(function (e) { return e.nodeType === Node.ELEMENT_NODE; }).length; // assignedElements does not exist in the polyfilled version so filtering nodes instead
    };
    /**
     * Toggles the active class of the list item.
     * @param {boolean} value The active state.
     */
    ListItemAdapter.prototype.setActive = function (value) {
        if (value) {
            addClass(LIST_ITEM_CONSTANTS.classes.ACTIVE, this._listItemElement);
        }
        else {
            removeClass(LIST_ITEM_CONSTANTS.classes.ACTIVE, this._listItemElement);
        }
    };
    /**
     * Toggles the selected class of the list item.
     * @param {boolean} value The active state.
     */
    ListItemAdapter.prototype.setSelected = function (value) {
        if (value) {
            addClass(LIST_ITEM_CONSTANTS.classes.SELECTED, this._listItemElement);
            // We are treating selected and activated as the same state, and mdc-states hooks right into --activated
            addClass(LIST_ITEM_CONSTANTS.classes.ACTIVATED, this._listItemElement);
        }
        else {
            removeClass(LIST_ITEM_CONSTANTS.classes.SELECTED, this._listItemElement);
            removeClass(LIST_ITEM_CONSTANTS.classes.ACTIVATED, this._listItemElement);
        }
    };
    /**
     * Attemps to toggle a checkbox or radio button within the list item if it can find one.
     */
    ListItemAdapter.prototype.tryToggleCheckboxRadio = function () {
        var checkable = this._component.querySelector(LIST_ITEM_CONSTANTS.selectors.CHECKBOX_RADIO_SELECTOR);
        if (checkable) {
            checkable.checked = !checkable.checked;
            checkable.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };
    /** Attempts to set focus to this list item. */
    ListItemAdapter.prototype.setFocus = function () {
        this._listItemElement.focus();
    };
    /** Returns whether the component has focus or not. */
    ListItemAdapter.prototype.hasFocus = function () {
        return document.activeElement === this._component || !!this._component.shadowRoot.activeElement;
    };
    /**
     * Sets the role on the list item element.
     * @param role The role.
     */
    ListItemAdapter.prototype.setRole = function (role) {
        this._listItemElement.setAttribute('role', role);
    };
    ListItemAdapter.prototype.setDisabled = function (disabled) {
        toggleClass(this._listItemElement, disabled, LIST_ITEM_CONSTANTS.classes.DISABLED);
    };
    ListItemAdapter.prototype.setDense = function (dense) {
        toggleClass(this._listItemElement, dense, LIST_ITEM_CONSTANTS.classes.DENSE);
    };
    ListItemAdapter.prototype.setIndented = function (indented) {
        toggleClass(this._listItemElement, indented, LIST_ITEM_CONSTANTS.classes.INDENTED);
    };
    ListItemAdapter.prototype.setWrap = function (value) {
        toggleClass(this._listItemElement, !value, LIST_ITEM_CONSTANTS.classes.NO_WRAP);
    };
    ListItemAdapter.prototype.trySelect = function (value) {
        var list = requireParent(this._component, LIST_CONSTANTS.elementName);
        if (!list || list.selectedValue === undefined) {
            return;
        }
        var listValues = list.selectedValue instanceof Array ? list.selectedValue : [list.selectedValue];
        if (listValues.some(function (v) { return isDeepEqual(v, value); })) {
            this.setSelected(true);
        }
    };
    return ListItemAdapter;
}(BaseAdapter));
export { ListItemAdapter };
